import { useState } from "react";
import api from "../../lib/api";
import { Card, Overline, Btn, Pill, Icon, EmptyState } from "../../components/ui-kit";
import { AiBlocks } from "../../components/AiBlocks";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const TYPES = [
  { id: "snapshot", label: "Snapshot", icon: "Zap" },
  { id: "quiz", label: "Quiz", icon: "CircleHelp" },
  { id: "flashcards", label: "Flashcards", icon: "Layers" },
  { id: "worksheet", label: "Worksheet", icon: "FileText" },
  { id: "lesson", label: "Lesson", icon: "BookOpen" },
  { id: "explanation", label: "Explanation", icon: "MessageSquareText" },
];

export default function AIStudio() {
  const [type, setType] = useState("quiz");
  const [form, setForm] = useState({ topic: "Ohm's Law", subject: "Science", difficulty: "medium", count: 5, instructions: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Enter a topic"); return; }
    setLoading(true); setResult(null); setStatus(null);
    try {
      const { data } = await api.post("/teacher/generate", { content_type: type, ...form, count: Number(form.count) });
      setResult(data.result); setStatus("ai_generated");
      toast.success("AI content generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed. Your content is safe.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-mim-purple/10 flex items-center justify-center"><Icon name="Sparkles" className="w-6 h-6 text-mim-purple" /></div>
        <div><Overline className="text-mim-purple">AI Content Studio</Overline><h1 className="text-2xl font-bold text-mim-navy">Create Content</h1></div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Config */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <Overline>What do you want to create?</Overline>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {TYPES.map((t) => (
                <button key={t.id} onClick={() => setType(t.id)} data-testid={`gen-type-${t.id}`}
                  className={cn("flex flex-col items-center gap-1.5 p-3 rounded-2xl border text-xs font-semibold transition-all",
                    type === t.id ? "border-mim-purple bg-mim-purple/5 text-mim-purple" : "border-mim-line text-mim-muted hover:border-mim-purple/40")}>
                  <Icon name={t.icon} className="w-5 h-5" />{t.label}
                </button>
              ))}
            </div>
          </Card>
          <Card className="space-y-3">
            <Field label="Topic"><input data-testid="gen-topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Subject"><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} /></Field>
              <Field label="Difficulty">
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className={inputCls}>
                  <option>easy</option><option>medium</option><option>hard</option>
                </select>
              </Field>
            </div>
            {["quiz", "flashcards", "worksheet"].includes(type) && (
              <Field label="Number of items"><input type="number" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className={inputCls} /></Field>
            )}
            <Field label="Custom instructions">
              <textarea data-testid="gen-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3}
                placeholder="e.g. Use household examples, keep it simple…" className={cn(inputCls, "resize-none")} />
            </Field>
            <Btn variant="ai" onClick={generate} disabled={loading} className="w-full" icon={loading ? "LoaderCircle" : "Sparkles"} data-testid="generate-btn">
              <span className={loading ? "animate-pulse" : ""}>{loading ? "Generating…" : "Generate"}</span>
            </Btn>
          </Card>
        </div>

        {/* Output */}
        <div className="lg:col-span-3">
          <Card className="min-h-[400px]">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-80 text-center">
                <Icon name="Sparkles" className="w-10 h-10 text-mim-purple animate-pulse mb-3" />
                <p className="font-semibold text-mim-navy">Generating your {type}…</p>
                <p className="text-sm text-mim-muted mt-1">Mimi is crafting curriculum-aligned content</p>
              </div>
            ) : !result ? (
              <EmptyState icon="Wand2" title="Nothing generated yet" subtitle="Configure options and hit Generate. Content is stored so it can be reused." />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Pill tone="purple" icon="Sparkles">AI Generated</Pill>
                  <div className="flex gap-2">
                    <Btn variant="secondary" onClick={generate} icon="RefreshCw" className="px-3 py-2">Regenerate</Btn>
                    <Btn variant="green" onClick={() => { setStatus("approved"); toast.success("Approved & published"); }} icon="Check" className="px-3 py-2">Approve</Btn>
                  </div>
                </div>
                {status === "approved" && <div className="text-sm text-mim-green font-medium flex items-center gap-1.5"><Icon name="CircleCheck" className="w-4 h-4" />Published — now visible to students.</div>}
                <GeneratedView type={type} result={result} />
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3.5 py-2.5 rounded-xl border border-mim-line focus:border-mim-purple focus:ring-2 focus:ring-mim-purple/15 outline-none text-sm bg-white";
function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-mim-navy block mb-1.5">{label}</label>{children}</div>;
}

function GeneratedView({ type, result }) {
  if (type === "quiz" && result.questions)
    return result.questions.map((q, i) => (
      <Card key={i} className="bg-mim-bg">
        <p className="font-semibold text-mim-navy text-sm">{i + 1}. {q.question}</p>
        <div className="grid sm:grid-cols-2 gap-1.5 mt-2">
          {(q.options || []).map((o) => <div key={o} className={cn("text-sm px-3 py-1.5 rounded-lg border", o === q.answer ? "border-mim-green bg-mim-green/10 text-mim-green font-semibold" : "border-mim-line text-mim-navy")}>{o}</div>)}
        </div>
        {q.explanation && <p className="text-xs text-mim-muted mt-2"><span className="font-semibold">Why: </span>{q.explanation}</p>}
      </Card>
    ));
  if (type === "flashcards" && result.flashcards)
    return <div className="grid sm:grid-cols-2 gap-3">{result.flashcards.map((f, i) => (
      <Card key={i} className="bg-mim-bg"><p className="font-semibold text-mim-navy text-sm">{f.front}</p><p className="text-sm text-mim-muted mt-2 pt-2 border-t border-mim-line">{f.back}</p></Card>
    ))}</div>;
  if (type === "snapshot" && result.one_line)
    return (
      <div className="space-y-3">
        <div className="bg-mim-navy text-white rounded-2xl p-4"><p>{result.one_line}</p><div className="text-xl font-bold mt-2">{result.formula}</div></div>
        <Card className="bg-mim-bg"><Overline>Key Ideas</Overline><ul className="mt-2 space-y-1 text-sm text-mim-navy">{(result.key_ideas||[]).map((k,i)=><li key={i}>• {k}</li>)}</ul></Card>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Card className="bg-mim-green/5"><Overline className="text-mim-green">Real life</Overline><p className="mt-1 text-mim-navy">{result.real_life}</p></Card>
          <Card className="bg-mim-yellow/10"><Overline className="text-[#b07d13]">Exam tip</Overline><p className="mt-1 text-mim-navy">{result.exam_tip}</p></Card>
        </div>
      </div>
    );
  if (type === "worksheet" && result.questions)
    return (
      <div>
        <h3 className="font-bold text-mim-navy text-lg">{result.title}</h3>
        <p className="text-sm text-mim-muted mb-3">{result.instructions}</p>
        <ol className="space-y-2 list-decimal list-inside">{result.questions.map((q, i) => <li key={i} className="text-sm text-mim-navy">{q.question} <span className="text-mim-muted">[{q.marks}m]</span></li>)}</ol>
      </div>
    );
  if (result.blocks) return <AiBlocks blocks={result.blocks} />;
  return <pre className="text-xs bg-mim-bg p-4 rounded-xl overflow-auto">{JSON.stringify(result, null, 2)}</pre>;
}
